import { Link } from 'react-router-dom';
import QuoteCTA from '../components/QuoteCTA.jsx';
import { heroMedia } from '../utils/mediaConfig.js';

const industries = [
  { href: '/oil-gas', image: 'oil-gas-wellsite.jpg', alt: 'Drilling rig at dusk with threaded steel tubulars racked in the foreground', title: 'Oil & Gas', text: 'Perforating gun carriers, endplates, bulkheads, feedthroughs and connectors for wireline and completion tools.', action: 'View oil & gas components' },
  { href: '/data-centers', image: 'data-hall.jpg', alt: 'Data hall under construction', title: 'Data Centers', text: 'Supports, enclosures, cable management, duct bank materials and custom parts for every phase of the build.', action: 'View data center products' },
  { href: '/industrial', image: 'turned-part.jpg', alt: 'CNC-turned stainless steel part', title: 'Industrial & Process Equipment', text: 'Machined, cast, stamped and molded parts for OEMs building heaters, pumps, compressors and valves.', action: 'View industrial equipment' },
];

const capabilities = [
  ['turned-part.jpg', 'CNC-turned stainless steel part', 'CNC machining', 'Multi-axis milling and turning for steel, stainless, aluminum and alloys.'],
  ['injection-mold.jpg', 'Open injection mold with molded housings', 'Injection molding', 'Engineering plastics, overmolding and insert molding, from tooling to volume.'],
  ['die-cast-brackets.jpg', 'Die-cast aluminum brackets', 'Die casting', 'Aluminum and zinc castings, machined and finished to drawing.'],
  ['laser-cutting.jpg', 'Fiber laser cutting sheet metal', 'Sheet metal', 'Laser cutting, bending, stamping and welded assemblies.'],
  ['anodized-parts.jpg', 'Anodized aluminum parts on racks', 'Finishing and heat treat', 'Anodizing, plating, coating, hardening and tempering, managed in one order.'],
  ['pcb-assembly.jpg', 'Assembled printed circuit boards in an ESD tray', 'PCB boards', 'Bare boards and full PCB assembly, from prototype runs to production volume.'],
  ['assembly-bench.jpg', 'Precision sub-assembly on a workbench', 'Assembly', 'Sub-assemblies and kitted systems that arrive ready to install.'],
];

const process = [
  ['1', 'Drawing review', 'We check tolerances, materials and cost drivers, and quote.'],
  ['2', 'First articles', 'Samples are made and measured before anything goes to volume.'],
  ['3', 'Production', 'Parts run at a partner plant matched to the process.'],
  ['4', 'Inspection', 'CMM reports and material certs travel with every lot.'],
  ['5', 'Duty-paid delivery', 'Customs, tariffs and freight are included. You receive the parts.'],
];

export default function Home() {
  return (
    <>
      <section className="relative h-[900px] tablet:h-auto tablet:min-h-[560px] tablet:px-6">
        <video autoPlay muted loop playsInline preload="metadata" poster={heroMedia.home.poster} aria-hidden="true" className="mx-hero-video">
          <source src={heroMedia.home.video} type="video/mp4" />
        </video>
        <div className="mx-hero-shade" />
        <div className="relative flex max-w-[900px] flex-col gap-8 px-[120px] pt-[250px] tablet:px-6 tablet:pt-[124px] tablet:pb-16">
          <p className="mx-section-label">CNC machining, injection molding and assembled components</p>
          <h1 className="m-0 font-display text-[84px] font-medium leading-[1.02] tracking-[-.02em] tablet:text-[44px] tablet:leading-[1.08]">Your parts, built to print overseas and delivered to your dock</h1>
          <p className="m-0 max-w-[660px] text-[20px] leading-[1.6] text-[#C9CAE0]">MachineX America manages the whole job for you: drawing review, production at vetted partner plants, U.S.-standard inspection, and duty-paid delivery. One contract, one point of contact.</p>
          <div className="mt-2 flex gap-4 phone:flex-wrap">
            <Link to="/contact" className="mx-button">Send us your drawing</Link>
            <a href="#process" className="mx-button-outline">See how it works</a>
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-12 px-[120px] pt-[140px] tablet:px-6">
        <div className="flex items-end justify-between gap-12 tablet:flex-wrap">
          <h2 className="mx-section-title max-w-[640px] text-[52px] tablet:text-[32px]">Industries we serve</h2>
          <p className="mx-lead max-w-[460px]">From downhole tools to data halls, our customers cannot afford late or out-of-spec parts. That is the work we are set up for.</p>
        </div>
        <div className="grid grid-cols-3 gap-8 tablet:grid-cols-1">
          {industries.map(({ href, image, alt, title, text, action }) => (
            <Link key={href} to={href} className="mx-card flex flex-col">
              <img src={`/media/images/${image}`} alt={alt} loading="lazy" className="block h-[360px] w-full border-0 object-cover" />
              <div className="flex flex-col gap-[14px] px-10 pb-10 pt-9">
                <h3 className="m-0 font-display text-[32px] font-semibold">{title}</h3>
                <p className="m-0 text-[17px] leading-[1.6] text-[#C9CAE0]">{text}</p>
                <span className="mt-2 font-display font-semibold text-[#F29A63]">{action}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-12 px-[120px] pt-40 tablet:px-6">
        <div className="flex items-end justify-between gap-12 tablet:flex-wrap">
          <h2 className="mx-section-title max-w-[640px] text-[52px] tablet:text-[32px]">Manufacturing capabilities</h2>
          <p className="mx-lead max-w-[460px]">Produced at vetted partner plants, inspected to U.S. standards and managed by us from drawing to delivery.</p>
        </div>
        <div className="grid grid-cols-4 gap-6 tablet:grid-cols-1">
          {capabilities.map(([image, alt, title, text]) => (
            <div key={title} className="flex flex-col gap-4">
              <img src={`/media/images/${image}`} alt={alt} loading="lazy" className="block h-[200px] w-full rounded-2xl object-cover" />
              <h3 className="m-0 font-display text-[22px] font-semibold">{title}</h3>
              <p className="m-0 text-[16px] leading-[1.6] text-[#B9BBDA]">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="process" className="mx-process-section mt-40 flex flex-col gap-14 px-[120px] py-[120px] tablet:mx-4 tablet:px-6">
        <div className="flex items-end justify-between gap-12 tablet:flex-wrap">
          <h2 className="mx-section-title max-w-[640px] text-[52px] tablet:text-[32px]">From drawing to your dock in five steps</h2>
          <p className="mx-lead max-w-[440px]">You deal with one U.S. company the whole way. We handle the factories, the paperwork and the freight.</p>
        </div>
        <ol className="m-0 grid list-none grid-cols-5 gap-0 border-t-2 border-[#E8773A] p-0 tablet:grid-cols-1">
          {process.map(([number, title, text]) => (
            <li key={number} className="flex flex-col gap-3 pt-7 pr-6 pb-0 first:pl-0 last:pr-0">
              <span className="font-display text-[40px] font-medium text-[#F29A63]">{number}</span>
              <strong className="font-display text-[20px] font-semibold">{title}</strong>
              <span className="text-[15px] leading-[1.6] text-[#B9BBDA]">{text}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="grid grid-cols-2 items-center gap-16 px-[120px] pt-[120px] tablet:grid-cols-1 tablet:px-6">
        <img src="/media/images/cmm-inspection.jpg" alt="CMM probe measuring a machined part" loading="lazy" className="block h-[420px] w-full rounded-3xl object-cover" />
        <div className="flex flex-col gap-6">
          <h2 className="mx-section-title text-[44px] leading-[1.12] tablet:text-[32px]">Inspected before it ships, documented when it arrives</h2>
          <p className="m-0 text-[18px] leading-[1.6] text-[#C9CAE0]">Every qualified lot ships with dimensional reports and material traceability. Our manufacturing partners hold the certifications below.</p>
          <div className="flex flex-wrap gap-3">
            {['ISO 9001', 'ISO 14001', 'IATF 16949'].map((label) => <span key={label} className="rounded-[10px] border border-[#3A3F86] px-4 py-[10px] font-display font-semibold">{label}</span>)}
          </div>
        </div>
      </section>

      <QuoteCTA title="Have a drawing? Get a quote." text="Send a PDF or STEP file with quantities. We reply within 24 hours with engineering questions or a price." action="Request a quote" />
    </>
  );
}
